"use strict";
/**
 *
 * Popover
 *
 * https://github.com/mui-org/material-ui/blob/b18a78e8cdad5894e96e65d6451545e075ed9d15/packages/material-ui/src/Popover/Popover.js
 *
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const react_dom_1 = __importDefault(require("react-dom"));
const clsx_1 = __importDefault(require("clsx"));
const utils_1 = require("../utils");
const styles_1 = require("../styles");
const Modal_1 = require("../Modal");
const Grow_1 = require("../Grow");
const Paper_1 = require("../Paper");
function getOffsetTop(rect, vertical) {
    let offset = 0;
    if (typeof vertical === 'number') {
        offset = vertical;
    }
    else if (vertical === 'center') {
        offset = rect.height / 2;
    }
    else if (vertical === 'bottom') {
        offset = rect.height;
    }
    return offset;
}
function getOffsetLeft(rect, horizontal) {
    let offset = 0;
    if (typeof horizontal === 'number') {
        offset = horizontal;
    }
    else if (horizontal === 'center') {
        offset = rect.width / 2;
    }
    else if (horizontal === 'right') {
        offset = rect.width;
    }
    return offset;
}
function getTransformOriginValue(transformOrigin) {
    return [transformOrigin.horizontal, transformOrigin.vertical]
        .map((n) => (typeof n === 'number' ? `${n}px` : n))
        .join(' ');
}
// Sum the scrollTop between two elements.
function getScrollParent(parent, child) {
    let element = child;
    let scrollTop = 0;
    while (element && element !== parent) {
        element = element.parentNode;
        scrollTop += element.scrollTop;
    }
    return scrollTop;
}
function getAnchorEl(anchorEl) {
    return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}
const styles = styles_1.createStyles({
    /* Styles applied to the `Paper` component. */
    paper: {
        position: 'absolute',
        overflowY: 'auto',
        overflowX: 'hidden',
        // So we see the popover when it's empty.
        // It's most likely on issue on userland.
        minWidth: 16,
        minHeight: 16,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: 'calc(100% - 32px)',
        // We disable the focus ring for mouse, touch and keyboard users.
        outline: 0,
    },
});
const Popover = react_1.default.forwardRef(function Popover(props, ref) {
    const { action, anchorEl, anchorOrigin = {
        vertical: 'top',
        horizontal: 'left',
    }, anchorPosition, anchorReference = 'anchorEl', children, classes, container: containerProp, elevation = 8, getContentAnchorEl, marginThreshold = 16, onEnter, onEntered, onEntering, onExit, onExited, onExiting, open, PaperProps = {}, transformOrigin = {
        vertical: 'top',
        horizontal: 'left',
    }, TransitionComponent = Grow_1.Grow, transitionDuration: transitionDurationProp = 'auto', TransitionProps = {} } = props, other = __rest(props, ["action", "anchorEl", "anchorOrigin", "anchorPosition", "anchorReference", "children", "classes", "container", "elevation", "getContentAnchorEl", "marginThreshold", "onEnter", "onEntered", "onEntering", "onExit", "onExited", "onExiting", "open", "PaperProps", "transformOrigin", "TransitionComponent", "transitionDuration", "TransitionProps"]);
    const paperRef = react_1.default.useRef();
    // Returns the top/left offset of the position
    // to attach to on the anchor element (or body if none is provided)
    const getAnchorOffset = react_1.default.useCallback((contentAnchorOffset) => {
        if (anchorReference === 'anchorPosition') {
            if (!anchorPosition) {
                // eslint-disable-next-line no-console
                console.warn('@domonda/ui: you need to provide a `anchorPosition` prop when using ' +
                    '<Popover anchorReference="anchorPosition" />.');
            }
            return anchorPosition;
        }
        const resolvedAnchorEl = getAnchorEl(anchorEl);
        const containerWindow = utils_1.ownerWindow(resolvedAnchorEl);
        // If an anchor element wasn't provided, just use the parent body element of this Popover
        const anchorElement = resolvedAnchorEl instanceof containerWindow.Element
            ? resolvedAnchorEl
            : utils_1.ownerDocument(paperRef.current).body;
        const anchorRect = anchorElement.getBoundingClientRect();
        const anchorVertical = contentAnchorOffset === 0 ? anchorOrigin.vertical : 'center';
        return {
            top: anchorRect.top + getOffsetTop(anchorRect, anchorVertical),
            left: anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal),
        };
    }, [anchorEl, anchorOrigin.horizontal, anchorOrigin.vertical, anchorPosition, anchorReference]);
    // Returns the vertical offset of inner content to anchor the transform on if provided
    const getContentAnchorOffset = react_1.default.useCallback((element) => {
        let contentAnchorOffset = 0;
        if (getContentAnchorEl && anchorReference === 'anchorEl') {
            const contentAnchorEl = getContentAnchorEl(element);
            if (contentAnchorEl && element.contains(contentAnchorEl)) {
                const scrollTop = getScrollParent(element, contentAnchorEl);
                contentAnchorOffset =
                    contentAnchorEl.offsetTop + contentAnchorEl.clientHeight / 2 - scrollTop || 0;
            }
            // != the default value
            if (anchorOrigin.vertical !== 'top') {
                // eslint-disable-next-line no-console
                console.warn([
                    '@domonda/ui: you can not change the default `anchorOrigin.vertical` value ',
                    'when also providing the `getContentAnchorEl` prop to the popover component.',
                    'Only use one of the two props.',
                    'Set `getContentAnchorEl` to `null | undefined`' +
                        ' or leave `anchorOrigin.vertical` unchanged.',
                ].join('\n'));
            }
        }
        return contentAnchorOffset;
    }, [anchorOrigin.vertical, anchorReference, getContentAnchorEl]);
    // Return the base transform origin using the element
    // and taking the content anchor offset into account if in use
    const getTransformOrigin = react_1.default.useCallback((elemRect, contentAnchorOffset = 0) => {
        return {
            vertical: getOffsetTop(elemRect, transformOrigin.vertical) + contentAnchorOffset,
            horizontal: getOffsetLeft(elemRect, transformOrigin.horizontal),
        };
    }, [transformOrigin.horizontal, transformOrigin.vertical]);
    const getPositioningStyle = react_1.default.useCallback((element) => {
        // Check if the parent has requested anchoring on an inner content node
        const contentAnchorOffset = getContentAnchorOffset(element);
        const elemRect = {
            width: element.offsetWidth,
            height: element.offsetHeight,
        };
        // Get the transform origin point on the element itself
        const elemTransformOrigin = getTransformOrigin(elemRect, contentAnchorOffset);
        if (anchorReference === 'none') {
            return {
                top: null,
                left: null,
                transformOrigin: getTransformOriginValue(elemTransformOrigin),
            };
        }
        // Get the offset of of the anchoring element
        const anchorOffset = getAnchorOffset(contentAnchorOffset);
        // Calculate element positioning
        let top = anchorOffset.top - elemTransformOrigin.vertical;
        let left = anchorOffset.left - elemTransformOrigin.horizontal;
        const bottom = top + elemRect.height;
        const right = left + elemRect.width;
        // Use the parent window of the anchorEl if provided
        const containerWindow = utils_1.ownerWindow(getAnchorEl(anchorEl));
        // Window thresholds taking required margin into account
        const heightThreshold = containerWindow.innerHeight - marginThreshold;
        const widthThreshold = containerWindow.innerWidth - marginThreshold;
        // Check if the vertical axis needs shifting
        if (top < marginThreshold) {
            const diff = top - marginThreshold;
            top -= diff;
            elemTransformOrigin.vertical += diff;
        }
        else if (bottom > heightThreshold) {
            const diff = bottom - heightThreshold;
            top -= diff;
            elemTransformOrigin.vertical += diff;
        }
        if (!(elemRect.height <= heightThreshold || !elemRect.height || !heightThreshold)) {
            // eslint-disable-next-line no-console
            console.warn([
                '@domonda/ui: the popover component is too tall.',
                `Some part of it can not be seen on the screen (${elemRect.height -
                    heightThreshold}px).`,
                'Please consider adding a `max-height` to improve the user-experience.',
            ].join('\n'));
        }
        // Check if the horizontal axis needs shifting
        if (left < marginThreshold) {
            const diff = left - marginThreshold;
            left -= diff;
            elemTransformOrigin.horizontal += diff;
        }
        else if (right > widthThreshold) {
            const diff = right - widthThreshold;
            left -= diff;
            elemTransformOrigin.horizontal += diff;
        }
        return {
            top: `${top}px`,
            left: `${left}px`,
            transformOrigin: getTransformOriginValue(elemTransformOrigin),
        };
    }, [
        anchorEl,
        anchorReference,
        getAnchorOffset,
        getContentAnchorOffset,
        getTransformOrigin,
        marginThreshold,
    ]);
    const setPositioningStyles = react_1.default.useCallback((element) => {
        const positioning = getPositioningStyle(element);
        if (positioning.top !== null) {
            element.style.top = positioning.top;
        }
        if (positioning.left !== null) {
            element.style.left = positioning.left;
        }
        element.style.transformOrigin = positioning.transformOrigin;
    }, [getPositioningStyle]);
    const handleEntering = (element, isAppearing) => {
        if (onEntering) {
            onEntering(element, isAppearing);
        }
        setPositioningStyles(element);
    };
    const handlePaperRef = react_1.default.useCallback((instance) => {
        // #StrictMode ready
        // eslint-disable-next-line react/no-find-dom-node
        paperRef.current = react_dom_1.default.findDOMNode(instance);
    }, []);
    const updatePosition = react_1.default.useMemo(() => {
        if (!open) {
            return undefined;
        }
        return utils_1.debounce(() => {
            setPositioningStyles(paperRef.current);
        });
    }, [open, setPositioningStyles]);
    react_1.default.useImperativeHandle(action, () => (open ? { updatePosition } : null), [
        open,
        updatePosition,
    ]);
    react_1.default.useEffect(() => {
        if (!updatePosition) {
            return undefined;
        }
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('resize', updatePosition);
            updatePosition.clear();
        };
    }, [updatePosition]);
    // If the container prop is provided, use that
    // If the anchorEl prop is provided, use its parent body element as the container
    // If neither are provided let the Modal take care of choosing the container
    const container = containerProp || (anchorEl ? utils_1.ownerDocument(getAnchorEl(anchorEl)).body : undefined);
    return (react_1.default.createElement(Modal_1.Modal, Object.assign({ container: container, open: open, ref: ref, BackdropProps: { invisible: true } }, other),
        react_1.default.createElement(TransitionComponent, Object.assign({ appear: true, in: open, onEnter: onEnter, onEntered: onEntered, onExit: onExit, onExited: onExited, onExiting: onExiting, timeout: transitionDurationProp }, TransitionProps, { onEntering: utils_1.createChainedFunction(handleEntering, TransitionProps.onEntering) }),
            react_1.default.createElement(Paper_1.Paper, Object.assign({ elevation: elevation, ref: handlePaperRef }, PaperProps, { className: clsx_1.default(classes.paper, PaperProps.className) }), children))));
});
if (process.env.NODE_ENV !== 'production') {
    Popover.displayName = 'Popover';
}
const StyledPopover = styles_1.withStyles(styles)(Popover);
exports.Popover = StyledPopover;
